import { CheckCircleOutlined, CloseCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import { Alert, Button, Card, message, Modal, Spin, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { firebaseFunctions, firestore } from '../../firebase/firebase';
import { FirebaseCallableFunctions, FirestoreCollection } from '../../firebase/firebase.models';
import { App } from '../App/App';
import { useAuth } from '../Auth/AuthContext';
import { AppRoutes } from '../Routing/routing.model';

const { Title, Text, Paragraph } = Typography;

interface AgencyAgreement {
  id: string;
  agentUid: string;
  offerId: string;
  ownerDetails: {
    name: string;
    address: string;
    peselOrNip: string;
    contact: string;
    email: string;
  };
  status: 'pending' | 'signed' | 'rejected';
  agentSignedAt: any;
  ownerSignedAt?: any;
  agreementUrl?: string;
  signedAgreementUrl?: string;
  createdAt: any;
  // Embedded data to avoid permission issues
  agentData?: {
    firstName: string;
    lastName: string;
    email: string;
    professionalLicenseNumber?: string;
    company?: {
      name: string;
    };
  };
  offerData?: {
    id: string;
    name: string;
    address?: any;
  };
}

interface Offer {
  name: string;
  address?: {
    city?: string;
    street?: string;
    buildingNumber?: string;
    apartmentNumber?: string;
  };
}

interface Agent {
  firstName: string;
  lastName: string;
  email: string;
  professionalLicenseNumber?: string;
  company?: {
    name: string;
  };
}

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 4px;
  background: ${(props) =>
    props.status === 'signed' ? '#f6ffed' : props.status === 'rejected' ? '#fff1f0' : '#fffbe6'};
  color: ${(props) => (props.status === 'signed' ? '#52c41a' : props.status === 'rejected' ? '#ff4d4f' : '#faad14')};
  border: 1px solid
    ${(props) => (props.status === 'signed' ? '#b7eb8f' : props.status === 'rejected' ? '#ffccc7' : '#ffe58f')};
`;

const InfoSection = styled.div`
  margin: 24px 0;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
`;

export const AgencyAgreementAccept: React.FC = () => {
  const { agreementId } = useParams<{ agreementId: string }>();
  const history = useHistory();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreement, setAgreement] = useState<AgencyAgreement | null>(null);
  const [offer, setOffer] = useState<Offer | null>(null);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [permissionError, setPermissionError] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const hasConfirmedRef = useRef(false);
  const hasRequestedConfirmationRef = useRef(false);

  // Get token from URL query params
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const confirmationToken = urlParams.get('confirmationToken');

  useEffect(() => {
    if (!agreementId) return;

    const loadAgreement = async () => {
      try {
        // If we have a token, use the special function to get agreement
        if (token) {
          const getAgreementFunction = firebaseFunctions.httpsCallable(
            FirebaseCallableFunctions.GET_AGENCY_AGREEMENT_BY_TOKEN
          );
          const result = await getAgreementFunction({ agreementId, token });

          if (result.data.success) {
            const agreementData = result.data.agreement;
            setAgreement(agreementData);

            // Use embedded data
            if (agreementData.offerData) {
              setOffer(agreementData.offerData);
            }
            if (agreementData.agentData) {
              setAgent(agreementData.agentData);
            }

            // Users with access token don't need real-time updates:
            // - They use the link once to review and sign
            // - They receive email confirmation after signing
            // - Real-time listener would cause "deadline-exceeded" errors for long sessions
            // - Refreshing the page fetches latest data via cloud function
            // - Logged-in users (agent/owner without token) still get real-time updates

            setLoading(false);
            return; // No cleanup needed - no listener to unsubscribe
          }
        } else {
          // Try normal Firestore access (for logged-in users)
          const unsubscribe = firestore
            .collection(FirestoreCollection.AGENCY_AGREEMENTS)
            .doc(agreementId)
            .onSnapshot(
              async (doc) => {
                if (!doc.exists) {
                  setError('Pełnomocnictwo nie zostało znalezione');
                  setLoading(false);
                  return;
                }

                const agreementData = doc.data() as AgencyAgreement;
                agreementData.id = doc.id;
                setAgreement(agreementData);

                // Use embedded data instead of fetching separately
                if (agreementData.offerData) {
                  setOffer(agreementData.offerData as Offer);
                }

                if (agreementData.agentData) {
                  setAgent(agreementData.agentData as Agent);
                }

                setLoading(false);
              },
              (err) => {
                console.error('Error fetching agreement data:', err);
                // Ignore deadline-exceeded errors if we already have agreement data (likely means it was just signed)
                if (err.code === 'deadline-exceeded' && agreement) {
                  console.log('Deadline exceeded but agreement already loaded, likely just signed');
                  return;
                }
                // Check if it's a permission error
                if (err.code === 'permission-denied') {
                  setPermissionError(true);
                  setError(
                    'Brak uprawnień do wyświetlenia tej umowy. Upewnij się, że korzystasz z linku otrzymanego w emailu.'
                  );
                } else if (err.code !== 'deadline-exceeded') {
                  setError('Wystąpił błąd podczas ładowania danych umowy');
                }
                setLoading(false);
              }
            );

          return () => unsubscribe();
        }
      } catch (err: any) {
        console.error('Error loading agreement:', err);
        setError(err.message || 'Wystąpił błąd podczas ładowania umowy');
        setLoading(false);
      }
    };

    loadAgreement();
  }, [agreementId, token]); // Removed offer and agent from dependencies to prevent infinite loop

  // Auto-confirm when confirmationToken is present in URL
  useEffect(() => {
    if (!confirmationToken || !agreement) return;
    if (agreement.status !== 'pending') return; // Already signed/rejected
    if (isConfirming || hasConfirmedRef.current) return; // Guard against multiple calls

    const confirmWithToken = async () => {
      hasConfirmedRef.current = true; // Mark as started
      setIsConfirming(true);

      try {
        const confirmFunction = firebaseFunctions.httpsCallable(
          FirebaseCallableFunctions.CONFIRM_AGENCY_AGREEMENT_WITH_TOKEN
        );

        const result = await confirmFunction({
          agreementId,
          confirmationToken,
        });

        if (result.data.success) {
          message.success('Pełnomocnictwo zostało pomyślnie podpisane!');

          // Show success modal with PDF download
          Modal.success({
            title: 'Pełnomocnictwo zostało podpisane',
            content: (
              <div>
                <Paragraph>
                  Pełnomocnictwo zostało podpisane przez obie strony. Kopia dokumentu została wysłana na Twój adres
                  email.
                </Paragraph>
                {result.data.agreementUrl && (
                  <Button
                    type="primary"
                    icon={<FileTextOutlined />}
                    onClick={() => window.open(result.data.agreementUrl, '_blank')}>
                    Pobierz pełnomocnictwo (PDF)
                  </Button>
                )}
              </div>
            ),
            okText: 'Zamknij',
          });
        }
      } catch (error: any) {
        console.error('Error confirming agreement:', error);

        const errorMessage = error.message || 'Wystąpił błąd podczas potwierdzania pełnomocnictwa';

        message.error(errorMessage);

        // Show error modal
        Modal.error({
          title: 'Błąd potwierdzenia',
          content: errorMessage,
          okText: 'Zamknij',
        });
      } finally {
        setIsConfirming(false);
      }
    };

    confirmWithToken();
  }, [confirmationToken, agreement, agreementId]); // Removed isConfirming from dependencies

  const handleAccept = async () => {
    // Prevent multiple confirmation requests
    if (hasRequestedConfirmationRef.current) {
      message.warning('Prośba o potwierdzenie została już wysłana. Sprawdź swoją skrzynkę email.');
      return;
    }

    Modal.confirm({
      title: 'Akceptacja pełnomocnictwa',
      content: (
        <div>
          <Paragraph>Czy na pewno chcesz zaakceptować pełnomocnictwo?</Paragraph>
          <Paragraph>
            Po akceptacji, agent {agreement.agentData?.firstName || agent?.firstName}{' '}
            {agreement.agentData?.lastName || agent?.lastName} będzie mógł działać w Twoim imieniu w zakresie wynajmu
            tego lokalu.
          </Paragraph>
        </div>
      ),
      okText: 'Akceptuj pełnomocnictwo',
      cancelText: 'Anuluj',
      onOk: async () => {
        hasRequestedConfirmationRef.current = true; // Mark as requested
        setSubmitting(true);

        try {
          const requestConfirmationFunction = firebaseFunctions.httpsCallable(
            FirebaseCallableFunctions.REQUEST_AGENCY_AGREEMENT_CONFIRMATION
          );

          const result = await requestConfirmationFunction({ agreementId });

          if (result.data.success) {
            // Show modal AFTER email is sent
            Modal.info({
              title: 'Sprawdź swoją skrzynkę email',
              content: (
                <div>
                  <Paragraph>
                    Na adres <strong>{agreement.ownerDetails.email}</strong> wysłaliśmy email z linkiem potwierdzającym.
                  </Paragraph>
                  <Paragraph>
                    <strong>Kliknij w link w mailu</strong>, aby dokończyć proces podpisywania pełnomocnictwa.
                  </Paragraph>
                  <Paragraph style={{ fontSize: '12px', color: '#666' }}>Link jest ważny przez 24 godziny.</Paragraph>
                </div>
              ),
              okText: 'Rozumiem',
            });
            message.success('Link potwierdzający został wysłany na email', 3);
          }
        } catch (error: any) {
          console.error('Error requesting confirmation:', error);
          message.error(error.message || 'Wystąpił błąd podczas wysyłania emaila. Spróbuj ponownie później.', 5);
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const handleReject = async () => {
    Modal.confirm({
      title: 'Odrzucenie pełnomocnictwa',
      content: 'Czy na pewno chcesz odrzucić pełnomocnictwo? Tej decyzji nie można cofnąć.',
      okText: 'Odrzuć pełnomocnictwo',
      okType: 'danger',
      cancelText: 'Anuluj',
      onOk: async () => {
        setSubmitting(true);
        try {
          const acceptFunction = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.ACCEPT_AGENCY_AGREEMENT);

          const result = await acceptFunction({
            agreementId,
            accepted: false,
          });

          if (result.data.success) {
            message.info('Pełnomocnictwo zostało odrzucone');
            // Agreement will be updated via real-time listener
          }
        } catch (error: any) {
          console.error('Error rejecting agreement:', error);
          message.error(error.message || 'Wystąpił błąd podczas odrzucania umowy');
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const formatAddress = (address?: any) => {
    if (!address) return '';
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.buildingNumber) parts.push(address.buildingNumber);
    if (address.apartmentNumber) parts.push(`m. ${address.apartmentNumber}`);
    if (address.city) parts.push(address.city);
    return parts.join(' ');
  };

  const formatFirebaseDate = (timestamp: any): string => {
    if (!timestamp) return 'Brak daty';

    try {
      // Handle Firestore Timestamp object with standard format
      if (timestamp.seconds) {
        const date = new Date(timestamp.seconds * 1000);
        if (isNaN(date.getTime())) {
          return 'Nieprawidłowa data';
        }
        return date.toLocaleDateString('pl-PL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }

      // Handle Firestore Timestamp with underscore-prefixed format (from Cloud Functions)
      if (timestamp._seconds) {
        const date = new Date(timestamp._seconds * 1000);
        if (isNaN(date.getTime())) {
          return 'Nieprawidłowa data';
        }
        return date.toLocaleDateString('pl-PL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }

      // Handle if it's already a Date object
      if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString('pl-PL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }

      // Handle if it's a toDate() function (Firestore Timestamp)
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        const date = timestamp.toDate();
        return date.toLocaleDateString('pl-PL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }

      return 'Nieprawidłowa data';
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Błąd formatowania daty';
    }
  };

  if (isConfirming) {
    return (
      <App>
        <Container>
          <Card>
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <Spin size="large" />
              <Paragraph style={{ marginTop: '16px' }}>Potwierdzanie i podpisywanie pełnomocnictwa...</Paragraph>
              <Paragraph style={{ fontSize: '12px', color: '#666' }}>
                Proszę czekać, trwa przetwarzanie dokumentu.
              </Paragraph>
            </div>
          </Card>
        </Container>
      </App>
    );
  }

  if (loading) {
    return (
      <App>
        <Container>
          <Card>
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <Spin size="large" />
              <Paragraph style={{ marginTop: '16px' }}>Ładowanie danych umowy...</Paragraph>
            </div>
          </Card>
        </Container>
      </App>
    );
  }

  if (error) {
    return (
      <App>
        <Container>
          <Alert
            message={permissionError ? 'Brak dostępu' : 'Błąd'}
            description={
              permissionError ? (
                <div>
                  <p>{error}</p>
                  <p style={{ marginTop: '10px' }}>
                    {!currentUser ? (
                      <>
                        Musisz być zalogowany, aby wyświetlić ten dokument. Zaloguj się na konto powiązane z adresem
                        email właściciela nieruchomości.
                      </>
                    ) : (
                      <>
                        Upewnij się, że jesteś zalogowany na właściwe konto (konto właściciela nieruchomości, które
                        otrzymało link do dokumentu).
                      </>
                    )}
                  </p>
                </div>
              ) : (
                error
              )
            }
            type="error"
            showIcon
            action={<Button onClick={() => history.push(AppRoutes.HOME)}>Strona główna</Button>}
          />
        </Container>
      </App>
    );
  }

  if (!agreement) {
    return (
      <App>
        <Container>
          <Alert
            message="Nie znaleziono dokumentu"
            description="Pełnomocnictwo nie zostało znalezione lub link jest nieprawidłowy."
            type="warning"
            showIcon
            action={<Button onClick={() => history.push(AppRoutes.HOME)}>Powrót do strony głównej</Button>}
          />
        </Container>
      </App>
    );
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'signed':
        return 'Podpisana';
      case 'rejected':
        return 'Odrzucona';
      default:
        return 'Oczekuje na podpis';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'signed':
        return <CheckCircleOutlined />;
      case 'rejected':
        return <CloseCircleOutlined />;
      default:
        return null;
    }
  };

  return (
    <App>
      <Container>
        <Card>
          <Title level={2}>Pełnomocnictwo</Title>

          <StatusBadge status={agreement.status}>
            {getStatusIcon(agreement.status)}
            {getStatusText(agreement.status)}
          </StatusBadge>

          <InfoSection>
            <Title level={4}>Dane nieruchomości</Title>
            <Paragraph>
              <Text strong>Nazwa:</Text> {agreement.offerData?.name || offer?.name || 'Brak danych'}
            </Paragraph>
            {(agreement.offerData?.address || offer?.address) && (
              <Paragraph>
                <Text strong>Adres:</Text> {formatAddress(agreement.offerData?.address || offer?.address)}
              </Paragraph>
            )}
          </InfoSection>

          <InfoSection>
            <Title level={4}>Mocodawca (Właściciel)</Title>
            <Paragraph>
              <Text strong>Imię i nazwisko/Nazwa:</Text> {agreement.ownerDetails.name}
            </Paragraph>
            <Paragraph>
              <Text strong>Adres:</Text> {agreement.ownerDetails.address}
            </Paragraph>
            <Paragraph>
              <Text strong>PESEL/NIP:</Text> {agreement.ownerDetails.peselOrNip}
            </Paragraph>
            <Paragraph>
              <Text strong>Email:</Text> {agreement.ownerDetails.email}
            </Paragraph>
            <Paragraph>
              <Text strong>Podpis elektroniczny:</Text>{' '}
              {agreement.ownerSignedAt ? (
                <Text type="success">Podpisano {formatFirebaseDate(agreement.ownerSignedAt)}</Text>
              ) : (
                <Text type="secondary">Oczekuje na podpis</Text>
              )}
            </Paragraph>
          </InfoSection>

          {agreement.status === 'pending' && (
            <>
              <Alert
                message="Dokument wymaga Twojej akceptacji"
                description={`Agent ${agreement.agentData?.firstName || agent?.firstName} ${
                  agreement.agentData?.lastName || agent?.lastName
                } podpisał elektronicznie pełnomocnictwo.
                  Przejrzyj dokładnie dane i zaakceptuj lub odrzuć dokument.`}
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              {agreement.agreementUrl && (
                <div style={{ marginBottom: '24px' }}>
                  <Button
                    type="default"
                    icon={<FileTextOutlined />}
                    onClick={() => window.open(agreement.agreementUrl, '_blank')}
                    block>
                    Zobacz pełną treść dokumentu (PDF)
                  </Button>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleAccept}
                  loading={submitting}
                  disabled={submitting}
                  style={{ minWidth: '200px' }}>
                  Akceptuj pełnomocnictwo
                </Button>
                <Button
                  danger
                  size="large"
                  onClick={handleReject}
                  loading={submitting}
                  disabled={submitting}
                  style={{ minWidth: '200px' }}>
                  Odrzuć pełnomocnictwo
                </Button>
              </div>
            </>
          )}

          {agreement.status === 'signed' && (
            <>
              <Alert
                message="Pełnomocnictwo zostało podpisane"
                description="Pełnomocnictwo zostało podpisane przez obie strony i jest prawnie wiążące."
                type="success"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              {(agreement.signedAgreementUrl || agreement.agreementUrl) && (
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  onClick={() => window.open(agreement.signedAgreementUrl || agreement.agreementUrl, '_blank')}
                  block>
                  Pobierz podpisane pełnomocnictwo (PDF)
                </Button>
              )}
            </>
          )}

          {agreement.status === 'rejected' && (
            <Alert
              message="Pełnomocnictwo zostało odrzucone"
              description="Pełnomocnictwo zostało odrzucone i nie jest ważne."
              type="error"
              showIcon
            />
          )}
        </Card>
      </Container>
    </App>
  );
};
